import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  KeyboardSensor,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortablePatient from "./patients-item";
import {
  useGetLatestUpcomingUsersLazyQuery,
  useGetUpcomingUsersQuery,
  useUpdateUpcomingVisitTimesMutation,
} from "@/graphql/generated";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  clearPatientFocus,
  selectCurrentRoom,
  setPatientFocus,
  setPatientLine,
  setUpdatePatientLine,
} from "@/context/features/room/roomSlice";
import { PatientInLine } from "@/types/public";
import { useTranslation } from "react-i18next";
import { Role } from "@/types/role.enum";
import { RefreshCw, UserRoundSearch } from "lucide-react";
import { Button } from "@/components/ui/button";

import { cn } from "@/lib/utils";
import PatientItemPlaceholder from "./patient-item-placeholder";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
type Props = {
  onRefetchReady?: (refetch: () => void) => void;
  selectPatient: (data: {
    pid: string;
    name: string;
    visitTime: string;
    isPending: boolean;
  }) => void;
};
function PatientsList({ onRefetchReady, selectPatient }: Props) {
  const {
    roomData,
    patientLine = [],
    updatePatientLine,
  } = useSelector(selectCurrentRoom, shallowEqual);

  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const { data, loading, error, refetch } = useGetUpcomingUsersQuery({
    variables: { roomName: roomData?.name || "" },
    fetchPolicy: "cache-first",
    skip: !roomData?.name,
  });

  const [getLatestUpcomingUsers, { data: latestData, loading: updateLoading }] =
    useGetLatestUpcomingUsersLazyQuery({
      fetchPolicy: "network-only",
    });
  const [updateTimes, { loading: saving }] =
    useUpdateUpcomingVisitTimesMutation();

  const upcomingUsers = useMemo(() => {
    return (
      latestData?.room?.getLatestUpcomingUsers ??
      data?.room?.upcomingUsers ??
      []
    );
  }, [latestData?.room?.getLatestUpcomingUsers, data?.room?.upcomingUsers]);
  // }, [data?.room?.upcomingUsers]);

  const { guests, activeUsers, finishedUsers, idToIndex } = useMemo(() => {
    const line = patientLine ?? [];
    const guests = line.filter(
      (p) => !p.isDone && p.role.toUpperCase() === Role.GUEST
    );
    const activeUsers = line.filter(
      (p) => !p.isDone && p.role.toUpperCase() === Role.USER
    );
    const finishedUsers = line.filter(
      (p) => p.isDone && p.role.toUpperCase() === Role.USER
    );

    const idToIndex = new Map<string | number, number>();
    line.forEach((p, i) => idToIndex.set(p.id, i));

    return {
      guests,
      activeUsers,
      finishedUsers,
      idToIndex,
    };
  }, [patientLine]);

  const guestIds = useMemo(() => guests.map((p) => p.id), [guests]);
  const activeIds = useMemo(() => activeUsers.map((p) => p.id), [activeUsers]);
  const finishedIds = useMemo(
    () => finishedUsers.map((p) => p.id),
    [finishedUsers]
  );

  const isHHMM = useCallback(
    (s: string) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(s),
    []
  );
  const coerceId = useCallback((id: string | number) => String(id), []);

  const buildUpdatesFromReorder = useCallback(
    (
      oldArr: PatientInLine[] = [],
      newArr: PatientInLine[] = []
    ): {
      reassigned: PatientInLine[];
      updates: Array<{
        id: string;
        visitTime: string;
        end_time: string;
        start_time: string;
      }>;
    } => {
      if (!oldArr.length || !newArr.length) {
        const reassigned = newArr.map((p) => ({ ...p, id: coerceId(p.id) }));
        return { reassigned, updates: [] };
      }
      const times = oldArr.map((p) => p.visitTime);
      const valid = times.every(isHHMM);
      const reassigned = newArr.map((p, i) => ({
        ...p,
        id: coerceId(p.id),
        visitTime: valid ? times[i] : p.visitTime,
      }));
      const updates = reassigned.map((p) => ({
        id: p.id,
        visitTime: p.visitTime,
        start_time: p.start_local,
        end_time: p.end_local,
      }));
      return { reassigned, updates };
    },
    [coerceId, isHHMM]
  );

  // const selectPatient = useCallback(
  //   (data: { pid: string; name: string; visitTime: string }) => {
  //     console.log("selectPatient", data);

  //     setSelectedPatient(data);
  //     setShowModal(true);
  //   },
  //   []
  // );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !patientLine?.length) return;

      const oldIndex = idToIndex.get(active.id);
      const newIndex = idToIndex.get(over.id);

      if (oldIndex == null || newIndex == null || oldIndex === newIndex) {
        return;
      }

      const reordered = arrayMove(patientLine, oldIndex, newIndex);
      const { reassigned, updates } = buildUpdatesFromReorder(
        patientLine,
        reordered
      );

      const prev = patientLine;
      dispatch(setPatientLine(reassigned));

      try {
        if (updates.length) {
          const optimisticUsers = reassigned.map((u) => ({
            __typename: "UpcomingUserOutput" as const,
            id: u.id,
            fullName: u.fullName,
            avatarUrl: u.avatarUrl,
            role: u.role,
            visit_guest: u.visit_guest,
            approved: u.approved,
            visitTime: u.visitTime,
            wantsToJoin: u.wantsToJoin,
          }));

          await updateTimes({
            variables: { updates },
            optimisticResponse: {
              __typename: "Mutation",
              room: {
                __typename: "RoomMutations",
                updateUpcomingVisitTimes: optimisticUsers,
              },
            },
          });
        }

        if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
        refetchTimeoutRef.current = setTimeout(() => {
          refetch();
        }, 800);
      } catch (e) {
        console.error("updateUpcomingVisitTimes error:", e);
        dispatch(setPatientLine(prev));
      }
    },
    [
      patientLine,
      idToIndex,
      buildUpdatesFromReorder,
      dispatch,
      updateTimes,
      refetch,
    ]
  );

  const updateAction = useCallback(async () => {
    await refetch();
    dispatch(setUpdatePatientLine(false));
  }, [refetch, dispatch]);

  useEffect(() => {
    onRefetchReady?.(refetch);
  }, [refetch, onRefetchReady]);

  useEffect(() => {
    dispatch(setPatientLine(upcomingUsers));
  }, [upcomingUsers, dispatch]);

  useEffect(() => {
    const foundActiveUser = patientLine?.find(
      (p) =>
        p.approved &&
        !p.isDone &&
        !p.isPaused &&
        p.role.toUpperCase() === Role.USER
    );
    if (foundActiveUser && foundActiveUser.gv_id && foundActiveUser.hash) {
      dispatch(
        setPatientFocus({
          id: foundActiveUser.id,
          hash: `${foundActiveUser.gv_id}_${foundActiveUser.hash}`,
        })
      );
    } else {
      dispatch(clearPatientFocus());
    }
  }, [dispatch, patientLine]);

  useEffect(() => {
    if (updatePatientLine) updateAction();
  }, [updateAction, updatePatientLine]);

  useEffect(() => {
    return () => {
      if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current);
    };
  }, []);

  return (
    <div
      className={cn(
        "overflow-y-auto overflow-x-hidden h-full px-1 pt-3 pb-5",
        saving ? "opacity-90" : ""
      )}
    >
      {!!guests.length && !loading && (
        <div className="flex flex-col gap-2">
          <h6 className="text-foreground font-semibold ps-1">
            {t("guest_lists")}
          </h6>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={guestIds}
              strategy={verticalListSortingStrategy}
            >
              {guests.map((patient) => (
                <SortablePatient
                  isWaiting={false}
                  key={patient.id}
                  locked={true}
                  patient={patient}
                  action_call_back={refetch}
                  selectPatient={selectPatient}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div className="flex items-center gap-2 ps-1 mb-2">
        <h6 className="text-foreground font-semibold">{t("patients_line")}</h6>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                setIsRefreshing(true);
                await refetch();
                setIsRefreshing(false);
              }}
              disabled={isRefreshing}
            >
              <RefreshCw
                size={18}
                strokeWidth={2}
                className={isRefreshing ? "animate-spin" : ""}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{t("patients_list")}</TooltipContent>
        </Tooltip>
      </div>
      {!!activeUsers.length && !loading && (
        <div className="flex flex-col gap-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeIds}
              strategy={verticalListSortingStrategy}
            >
              {activeUsers.map((patient) => (
                <SortablePatient
                  isWaiting={false}
                  key={patient.id}
                  locked={!!patient.isDone}
                  patient={patient}
                  action_call_back={refetch}
                  selectPatient={selectPatient}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {!!finishedUsers.length && !loading && (
        <div className="mt-5">
          <h6 className="text-foreground font-semibold ps-1">
            {t("finished_vist")}
          </h6>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={finishedIds}
              strategy={verticalListSortingStrategy}
            >
              {finishedUsers.map((patient) => (
                <SortablePatient
                  isWaiting={false}
                  key={patient.id}
                  locked={!!patient.isDone}
                  patient={patient}
                  action_call_back={refetch}
                  selectPatient={selectPatient}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {loading && (
        <div className="flex flex-col gap-2 ">
          <PatientItemPlaceholder />
          <PatientItemPlaceholder />
          <PatientItemPlaceholder />
          <PatientItemPlaceholder />
          <PatientItemPlaceholder />
        </div>
      )}
      {!error && !loading && !upcomingUsers.length && (
        <div className="flex flex-col items-center gap-4 py-12 my-2">
          <UserRoundSearch
            className="size-12 text-muted-foreground"
            strokeWidth={1.75}
          />
          <p className="text-base text-muted-foreground text-center">
            {t("patient_not_found")}
          </p>
          <button
            onClick={() => getLatestUpcomingUsers()}
            disabled={updateLoading}
            className="text-sm text-primary hover:text-primary-hover inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw
              size={14}
              strokeWidth={2}
              className={updateLoading ? "animate-spin" : ""}
            />
            {t("retry")}
          </button>
        </div>
      )}
      {error && (
        <div className="flex flex-col gap-2 border border-destructive text-destructive p-3 rounded-md">
          <p className="text-center text-sm">
            {t("error_occurred_in_patients_list")}
          </p>
          <Button
            variant="ghost"
            onClick={() => refetch()}
            className="w-fit mx-auto flex items-center gap-2 text-destructive hover:bg-destructive/10"
          >
            <RefreshCw size={18} strokeWidth={2} />
            {t("retry")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default memo(PatientsList);
